import { ListView, Search, useSearch } from "@sembark-travel/ui/list"
import { dataDriveXHR, TSheetFile, TSheetFolder, useFileDetails } from "./store"
import {
	Alert,
	AsyncSelect,
	Box,
	Button,
	Component,
	Dropdown,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	Tabs,
	TabsList,
	Text,
	Time,
} from "@sembark-travel/ui/base"
import {
	Breadcrumbs,
	ButtonLink,
	Link,
	NavLink,
} from "@sembark-travel/ui/router"
import { generatePath } from "../router-utils"
import { useXHR } from "@sembark-travel/xhr"
import { useCallback, useRef, useState } from "react"
import { Dialog } from "@sembark-travel/ui/dialog"
import { CreateFolder } from "./NewItem"
import {
	DeleteSheetFileInDialog,
	EditSheetFileInDialog,
	EditSheetFolderInDialog,
} from "./Sheets"
import { TemporaryStorage } from "../TemporaryStorage"
import config from "../config"
import { TSPFileVersionDetails } from "./TransportService"
import { CabMetricPricesFileVersionDetails } from "./Metric"

export function FoldersViewList() {
	const [params, setParams] = useSearch()

	return (
		<Search
			title={"Data Drive"}
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			actions={
				<Inline gap="2">
					<Dropdown alignRight="sm">
						<Dropdown.ToggleButton level="tertiary">
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							<NavLink to={generatePath("/data-drive/folders/new/metric")}>
								Add Metric Sheets
							</NavLink>
							{/* <NavLink to={generatePath("/data-drive/folders/new/transport")}>
								Add Transport Sheet
							</NavLink> */}
						</Dropdown.Menu>
					</Dropdown>
				</Inline>
			}
		>
			<ListView<TSheetFolder>
				pageKey="manage-sheets-folder-lists"
				params={params}
				fetch={(xhr, params) => dataDriveXHR(xhr).getFolders(params)}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, refresh }) => (
					<Stack gap="1">
						{items.map((folder) => {
							const { transportSheets, metricsSheets } = folder.files.reduce<{
								transportSheets: TSheetFile[]
								metricsSheets: TSheetFile[]
							}>(
								(acc, file) => {
									if (file.contains === "transport_services") {
										acc.transportSheets.push(file)
									}
									if (file.contains === "cab_price_calculation_metrics") {
										acc.metricsSheets.push(file)
									}
									return acc
								},
								{ transportSheets: [], metricsSheets: [] }
							)

							return (
								<Stack
									key={folder.id}
									borderWidth="1"
									borderRadius="md"
									gap="2"
									padding="4"
									width="full"
									backgroundColor="default"
								>
									<Inline justifyContent="between">
										<Link
											to={
												folder.files.length
													? generatePath(
															"/data-drive/folders/:folderId/files/:fileId",
															{
																folderId: String(folder.id),
																fileId: String(folder.files[0].id),
															}
														)
													: generatePath("/data-drive/folders/:folderId", {
															folderId: String(folder.id),
														})
											}
											query={{ folder: folder.name }}
										>
											<Text fontSize="md" fontWeight="semibold">
												{folder.name}
											</Text>
										</Link>

										{folder.created_at && folder.created_by ? (
											<Box fontSize="sm" color="muted">
												Created by {folder.created_by.name} on{" "}
												<Time timestamp={folder.created_at} />
											</Box>
										) : null}
									</Inline>

									<Component initialState={false}>
										{({ state, setState }) => (
											<Box>
												<Button inline onClick={() => setState(!state)}>
													<Icons.ViewList />
													<Text fontSize="sm" fontWeight="semibold">
														Total Sheets {(folder.files || []).length}
													</Text>
												</Button>
												{state ? (
													<Stack gap={"1"} paddingLeft={"4"}>
														{transportSheets.length > 0 ? (
															<ShowFilesList
																sheets={transportSheets}
																title={"Transport sheets"}
																folderId={String(folder.id)}
																folderName={folder.name}
															/>
														) : null}
														{metricsSheets.length > 0 ? (
															<ShowFilesList
																sheets={metricsSheets}
																title={"Metrics sheets"}
																folderId={String(folder.id)}
																folderName={folder.name}
															/>
														) : null}
													</Stack>
												) : null}
											</Box>
										)}
									</Component>
								</Stack>
							)
						})}
					</Stack>
				)}
			</ListView>
		</Search>
	)
}
function ShowFilesList({
	sheets,
	title,
	folderId,
	folderName,
}: {
	sheets: TSheetFile[]
	title: string
	folderId: string
	folderName: string
}) {
	const [state, setState] = useState(false)
	return (
		<Box>
			<Button inline onClick={() => setState(!state)}>
				<Icons.ChevronDown rotate={state ? undefined : "270"} size="4" />
				<Text fontWeight="semibold">{title}</Text>
			</Button>
			{state ? (
				<Stack paddingLeft={"6"}>
					{sheets.map((file) => (
						<Link
							key={file.id}
							to={generatePath("/data-drive/folders/:folderId/files/:fileId", {
								folderId: folderId,
								fileId: String(file.id),
							})}
							query={{ folder: folderName }}
							color="accent"
						>
							<Icons.Document /> {file.name}
						</Link>
					))}
				</Stack>
			) : null}
		</Box>
	)
}

export function FileDetailsLayout({
	folderDetails,
	refreshFolderDetails,
	fileId,
	onSuccess,
	versionId,
}: {
	folderDetails: TSheetFolder
	refreshFolderDetails: () => void
	fileId: string
	versionId?: string
	onSuccess: () => void
}) {
	return (
		<>
			<Breadcrumbs
				title={folderDetails.name}
				items={[[generatePath("/data-drive"), "Data Drive"]]}
			/>
			<Stack gap={"1"}>
				<FolderDetails
					folderDetails={folderDetails}
					refreshFolderDetails={refreshFolderDetails}
				/>

				{folderDetails.files.length === 0 ? (
					<Alert status="info">
						This folder does not contain any files yet.
					</Alert>
				) : (
					<Box borderTopWidth="1">
						<Tabs layout="col" bgColor={"default"}>
							<TabsList>
								{(folderDetails.files || []).map((file) => (
									<NavLink
										id={String(file.id)}
										key={file.id}
										to={generatePath(
											"/data-drive/folders/:folderId/files/:fileId",
											{
												folderId: folderDetails.id.toString(),
												fileId: file.id.toString(),
											}
										)}
										query={{
											file: file.name,
											folder: folderDetails.name,
										}}
									>
										<Inline
											alignItems={"center"}
											gap="2"
											color={
												file.latest_version.is_draft ? "warning" : "success"
											}
											title={
												file.contains === "transport_services"
													? "Transport file"
													: "Metric file"
											}
										>
											{file.contains === "transport_services" ? (
												<Icons.Taxi />
											) : (
												<Icons.SteeringWheel />
											)}
											{file.name}
										</Inline>
									</NavLink>
								))}
							</TabsList>

							<TabContent>
								{folderDetails.files.length ? (
									<FileVersionsDetailsLayout
										folderId={String(folderDetails.id)}
										fileId={fileId}
										versionId={
											versionId ||
											String(
												folderDetails.files.find((f) => String(f.id) === fileId)
													?.latest_version.id || ""
											)
										}
										hideVersionTab={!versionId}
										onSuccess={onSuccess}
										refreshFolderDetails={refreshFolderDetails}
									/>
								) : (
									<Alert status="error">File does not exist in folder.</Alert>
								)}
							</TabContent>
						</Tabs>
					</Box>
				)}
			</Stack>
		</>
	)
}

export function FolderDetails({
	folderDetails,
	refreshFolderDetails,
}: {
	folderDetails: TSheetFolder
	refreshFolderDetails: () => void
}) {
	return (
		<Stack gap="2">
			<Stack
				key={folderDetails.id}
				borderWidth={"1"}
				paddingY="4"
				paddingX={"6"}
				width="full"
				backgroundColor={"default"}
			>
				<Inline justifyContent={"between"} gap={"2"}>
					<Stack>
						<Text fontSize={"md"}>{folderDetails.name}</Text>
						{folderDetails.created_at && folderDetails.created_by ? (
							<Inline gap="2">
								<Box fontSize={"sm"} color={"muted"}>
									Created by {folderDetails.created_by.name} on{" "}
									<Time timestamp={folderDetails.created_at} />
								</Box>
							</Inline>
						) : null}
					</Stack>
					<Dropdown alignRight="sm">
						<Dropdown.ToggleButton>
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							<NavLink
								to={generatePath("/data-drive/folders/new/metric")}
								query={{
									folder: folderDetails.id,
								}}
								state={{ folderDetails: folderDetails }}
							>
								<Icons.Sheet /> Add metric file
							</NavLink>
							{/* <NavLink
									to={generatePath("/data-drive/folders/new/transport")}
									query={{
										folder: folderDetails.id,
									}}
									state={{ folderDetails: folderDetails }}
								>
									<Icons.Sheet /> Add transport file
								</NavLink> */}
							<Dropdown.MenuItemDivider />
							<EditSheetFolderInDialog
								folder={folderDetails}
								onSuccess={refreshFolderDetails}
								editFolderDetails={(xhr, sheetFolderData, folderId) =>
									dataDriveXHR(xhr).updateFolder(
										String(folderId),
										sheetFolderData
									)
								}
							>
								{({ showEditDialog }) => (
									<Dropdown.MenuItem onClick={showEditDialog}>
										<Inline gap="2" alignItems={"center"}>
											<Icons.Pencil color={"muted"} />
											Rename Folder
										</Inline>
									</Dropdown.MenuItem>
								)}
							</EditSheetFolderInDialog>

							<TemporaryStorage
								initUrl={`/data-drive/folders/${folderDetails.id}/download`}
								initParams={{
									timezone_offset: config.timezoneOffset,
								}}
							>
								{({ message, generate, url, reset }) =>
									url ? (
										<Dropdown.MenuItem
											as="a"
											href={url}
											target="_blank"
											download
											onClick={() => reset()}
										>
											{message}
										</Dropdown.MenuItem>
									) : (
										<Dropdown.MenuItem
											onClick={(e: React.MouseEvent) => {
												e.preventDefault()
												generate()
											}}
										>
											{message || (
												<Inline gap="2" alignItems={"center"}>
													<Icons.DocumentDownload /> Download as CSV/Excel
												</Inline>
											)}
										</Dropdown.MenuItem>
									)
								}
							</TemporaryStorage>
						</Dropdown.Menu>
					</Dropdown>
				</Inline>
			</Stack>
			{folderDetails.files.length ? null : (
				<Alert status="info">This folder does not contain any files yet.</Alert>
			)}
		</Stack>
	)
}

export function FileVersionsDetailsLayout({
	fileId,
	folderId,
	versionId,
	onSuccess,
	refreshFolderDetails,
	hideVersionTab = true,
}: {
	fileId: string
	folderId: string
	versionId?: string
	onSuccess: () => void
	refreshFolderDetails: () => void
	hideVersionTab?: boolean
}) {
	const { fileDetails, refreshFileDetails, error, isLoading } =
		useFileDetails(fileId)

	if (isLoading && !fileDetails) return <Spinner alignCenter padding="4" />
	if (!fileDetails || error)
		return (
			<Alert status="error">
				{error?.message ||
					"The file doesn't exists. Please check the folder for more details."}
			</Alert>
		)
	if (!versionId && !fileDetails.latest_version.id) {
		return (
			<Alert status="error">
				Some thing went wrong. File doesn't have any sheet details.
			</Alert>
		)
	}
	versionId = versionId || String(fileDetails.latest_version.id)
	return (
		<Tabs bgColor={"default"} defaultActive={versionId}>
			{hideVersionTab ? null : (
				<TabsList>
					{fileDetails.versions.map((version, index) => (
						<NavLink
							id={String(version.id)}
							key={version.id}
							active={versionId === String(version.id)}
							to={generatePath(
								"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
								{
									folderId: folderId.toString(),
									fileId: fileId.toString(),
									versionId: version.id.toString(),
								}
							)}
							query={{
								fileName: fileDetails.name,
								folderName: fileDetails.folder?.name,
							}}
						>
							<Inline gap="2" alignItems={"center"}>
								{version.is_draft ? (
									<Icons.Sheet color={"warning"} size={"5"} />
								) : (
									<Icons.Sheet color={"primary"} size={"5"} />
								)}

								<Text>V - {(fileDetails.versions || []).length - index}</Text>
							</Inline>
						</NavLink>
					))}
				</TabsList>
			)}

			<TabContent>
				<Stack padding={"2"} gap={"2"}>
					{fileDetails.deleted_at ? (
						<Alert status="warning">
							The file you are viewing was deleted
							{fileDetails.deleted_by?.name
								? ` by ${fileDetails.deleted_by.name}`
								: ""}{" "}
							on <Time timestamp={fileDetails.deleted_at} />.
						</Alert>
					) : null}
					<Inline justifyContent={"between"} paddingX={"4"}>
						<Inline gap="2" alignItems={"center"}>
							<Icons.Sheet color={"primary"} size={"6"} />
							<Stack gap="1">
								<Text fontSize={"md"}>{fileDetails.name}</Text>
								<Inline gap={"1"}>
									<Text fontSize={"sm"} color={"muted"}>
										Created on <Time timestamp={fileDetails.created_at} />.
									</Text>
									{fileDetails.updated_at && fileDetails.updated_by ? (
										<Text fontSize={"sm"} color={"muted"}>
											Last updated by {fileDetails.updated_by.name} on{" "}
											<Time timestamp={fileDetails.updated_at} />
										</Text>
									) : null}
								</Inline>
							</Stack>
						</Inline>

						<Dropdown alignRight>
							<Dropdown.ToggleButton>
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{fileDetails.deleted_at ? null : (
									<EditSheetFileInDialog
										file={fileDetails}
										onSuccess={() => {
											refreshFileDetails()
											refreshFolderDetails()
										}}
										editFileDetails={(xhr, sheetFileData, fileId) =>
											dataDriveXHR(xhr).updateFile(
												String(fileId),
												sheetFileData
											)
										}
									>
										{({ showEditDialog }) => (
											<Dropdown.MenuItem onClick={showEditDialog}>
												<Inline gap="2" alignItems={"center"}>
													<Icons.Pencil color={"muted"} />
													Rename File
												</Inline>
											</Dropdown.MenuItem>
										)}
									</EditSheetFileInDialog>
								)}

								{hideVersionTab ? (
									<Dropdown.MenuItem as="div">
										<ButtonLink
											to={generatePath(
												"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
												{
													folderId,
													fileId,
													versionId,
												}
											)}
											query={{
												file: fileDetails.name,
												folder: fileDetails.folder?.name,
											}}
											level="secondary"
										>
											<Inline gap={"2"} alignItems={"center"}>
												<Icons.ViewList />

												<Text>View Versions</Text>
											</Inline>
										</ButtonLink>
									</Dropdown.MenuItem>
								) : null}

								{(fileDetails.versions || []).length > 1 ? (
									<Dropdown.MenuItem as="div">
										<ButtonLink
											to={generatePath(
												fileDetails.contains === "transport_services"
													? "/data-drive/folders/:folderId/files/:fileId/compare/:versionId1/:versionId2/transport"
													: "/data-drive/folders/:folderId/files/:fileId/compare/:versionId1/:versionId2/metric",
												{
													folderId: folderId,
													fileId: fileId,
													versionId1: String(fileDetails.latest_version.id),
													versionId2:
														String(fileDetails.latest_version.id) === versionId
															? String(fileDetails.versions[1].id)
															: versionId,
												}
											)}
											query={{
												file: fileDetails.name || "",
												folder: fileDetails.folder?.name || "",
											}}
										>
											<Inline gap="2" alignItems={"center"}>
												<Icons.SparklesSolid color={"muted"} />
												Compare File
											</Inline>
										</ButtonLink>
									</Dropdown.MenuItem>
								) : null}

								<TemporaryStorage
									initUrl={`/data-drive/files/${fileId}/download`}
									initParams={{
										timezone_offset: config.timezoneOffset,
									}}
								>
									{({ message, generate, url, reset }) =>
										url ? (
											<Dropdown.MenuItem
												as="a"
												href={url}
												target="_blank"
												download
												onClick={() => reset()}
											>
												{message}
											</Dropdown.MenuItem>
										) : (
											<Dropdown.MenuItem
												onClick={(e: React.MouseEvent) => {
													e.preventDefault()
													generate()
												}}
											>
												{message || (
													<Inline gap="2" alignItems={"center"}>
														<Icons.DocumentDownload /> Download as CSV/Excel
													</Inline>
												)}
											</Dropdown.MenuItem>
										)
									}
								</TemporaryStorage>

								{fileDetails.deleted_at ? null : (
									<DeleteSheetFileInDialog
										file={fileDetails}
										onSuccess={() => {
											onSuccess()
										}}
										deleteSheetFile={(xhr, fileId) => {
											return dataDriveXHR(xhr).deleteFile(String(fileId))
										}}
									>
										{({ onDelete, isDeleting }) => (
											<Dropdown.MenuItem
												color="danger"
												onClick={() => {
													if (isDeleting) return
													onDelete()
												}}
											>
												{isDeleting ? (
													<>Deleting...</>
												) : (
													<>
														<Inline gap="2" alignItems={"center"}>
															<Icons.Trash />
															Delete File
														</Inline>
													</>
												)}
											</Dropdown.MenuItem>
										)}
									</DeleteSheetFileInDialog>
								)}
							</Dropdown.Menu>
						</Dropdown>
					</Inline>

					{fileDetails.contains === "transport_services" ? (
						<TSPFileVersionDetails
							folderId={folderId}
							fileId={fileId.toString()}
							versionId={versionId}
						/>
					) : (
						<CabMetricPricesFileVersionDetails
							folderId={folderId}
							fileId={fileId}
							versionId={versionId}
						/>
					)}
				</Stack>
			</TabContent>
		</Tabs>
	)
}

export function SelectFolder({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	const [createItemName, setCreateName] = useState<undefined | string>(
		undefined
	)
	const waitingPromise = useRef<((val: TSheetFolder) => void) | undefined>(
		undefined
	)
	const isDialogOpen = createItemName !== undefined

	const fetchSheetFolders = useCallback(
		async (q: string) => {
			const resp = await dataDriveXHR(xhr).getFolders({ q: q })
			return resp.data
		},
		[xhr]
	)

	function closeDialog() {
		setCreateName(undefined)
	}

	return (
		<>
			<AsyncSelect
				placeholder="Select or create a folder..."
				optionRenderer={({
					option,
					created,
				}: {
					option: TSheetFolder
					created?: boolean
				}) =>
					created ? (
						<Text>Create folder: '{option.name}'</Text>
					) : (
						<Text>{option.name}</Text>
					)
				}
				{...props}
				fetch={fetchSheetFolders}
				onCreateNew={async (q) => {
					setCreateName(q || "")
					return new Promise((resolve) => {
						waitingPromise.current = resolve
					})
				}}
			/>

			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Create New Folder"
			>
				<Dialog.Body>
					<CreateFolder
						onSuccess={(item) => {
							waitingPromise.current?.(item)
							closeDialog()
						}}
						onCancel={closeDialog}
						initialValues={{
							name: createItemName || "",
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
